// Pink-bucket-only Socratic AI Tutor chat. One panel per question, mounted
// ONLY inside MasteryBoard's Pink "AI Tutor" tab (see MasteryBoard.tsx) — but
// the real gate lives server-side: src/routes/tutor.ts independently
// re-checks that this question is the calling student's own Pink card on
// every single turn, regardless of where the request came from. Stateless:
// nothing here is persisted server-side, so a page refresh clears the
// conversation.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiErrorMessage, apiPost } from '@/lib/api';

interface TutorTurn {
  role: 'student' | 'tutor';
  message: string;
}

interface TutorReplyResult {
  tutorMessage: string;
  suggestScaffold: boolean;
}

// Mirrors the Node route's cap (src/routes/tutor.ts's zod schema, max 8) —
// keeping the client's own history trimmed to the same bound avoids a
// surprise 400 on the turn that would have pushed it over.
const MAX_HISTORY = 8;

export function AITutorPanel({
  questionId,
  questionText,
  subtopicId,
}: {
  questionId: string;
  questionText: string;
  subtopicId?: string;
}) {
  const navigate = useNavigate();
  const [history, setHistory] = useState<TutorTurn[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [suggestScaffold, setSuggestScaffold] = useState(false);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setError('');
    const nextHistory: TutorTurn[] = [...history, { role: 'student', message: text } as TutorTurn].slice(-MAX_HISTORY);
    setHistory(nextHistory);
    setInput('');
    setSending(true);
    try {
      const result = await apiPost<TutorReplyResult>('/tutor/session', {
        questionId,
        studentHistory: nextHistory,
      });
      setHistory((h) => [...h, { role: 'tutor' as const, message: result.tutorMessage }].slice(-MAX_HISTORY));
      setSuggestScaffold(result.suggestScaffold);
    } catch (err: unknown) {
      setError(apiErrorMessage(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-card">
      <div className="border-b border-slate-100 px-3 py-2">
        <p className="text-sm font-semibold text-slate-700">AI Tutor</p>
        <p className="mt-0.5 line-clamp-1 text-xs text-slate-400">{questionText}</p>
      </div>

      <div className="max-h-56 space-y-2 overflow-y-auto p-3">
        {history.length === 0 && (
          <p className="text-sm text-slate-400">
            Stuck? Ask the tutor a question — it will guide you, not give the answer.
          </p>
        )}
        {history.map((turn, i) => (
          <div key={i} className={turn.role === 'student' ? 'text-right' : 'text-left'}>
            <span
              className={[
                'inline-block max-w-[85%] rounded-2xl px-3 py-2 text-sm',
                turn.role === 'student' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-ink',
              ].join(' ')}
            >
              {turn.message}
            </span>
          </div>
        ))}
        {sending && <p className="text-xs text-slate-400">Tutor is thinking…</p>}
      </div>

      {suggestScaffold && subtopicId && (
        <div className="border-t border-slate-100 bg-bin-orange-bg px-3 py-2 text-xs text-bin-orange-text">
          Still stuck?{' '}
          <button
            type="button"
            onClick={() => navigate(`/learn/basics/${subtopicId}`)}
            className="font-semibold underline"
          >
            Try Scaffold Mode
          </button>{' '}
          to rebuild this from the basics.
        </div>
      )}

      {error && <p className="px-3 py-1 text-xs text-danger">{error}</p>}

      <div className="flex gap-2 border-t border-slate-100 p-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && send()}
          placeholder="Ask the tutor…"
          maxLength={1_000}
          className="flex-1 rounded-[var(--control-radius)] border border-line px-3 py-2 text-sm outline-none focus:border-forest"
          disabled={sending}
        />
        <button
          type="button"
          onClick={send}
          disabled={sending || !input.trim()}
          className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white disabled:opacity-40"
        >
          Send
        </button>
      </div>
    </div>
  );
}
